import React from 'react';
import { cn } from '@/utils';
import { Icon } from '@/components';
import { TAG_LABELS, WORDS } from '@/constants';

export type StatusType =
  | 'certified'
  | 'failed-1st-attempt'
  | 'failed-2nd-attempt'
  | 'no-attempts'
  | 'failed-practice'
  | 'pass'
  | 'fail';

export interface StatusBadgeProps {
  /** The status type to display */
  status: StatusType;
  /** Additional CSS classes */
  className?: string;
  /** Optional custom label override */
  label?: string;
}

/**
 * StatusBadge Component
 *
 * Displays status indicators with appropriate colors and icons
 * based on the Figma design system.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className, label }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'certified':
        return {
          label: label || TAG_LABELS.CERTIFIED,
          bgColor: 'bg-accent-surface-green',
          textColor: 'text-neutral-black',
          icon: 'Medal' as const,
          iconColor: 'text-accent-status-green',
        };
      case 'failed-1st-attempt':
        return {
          label: label || TAG_LABELS.FAIL_1ST_ATTEMPT,
          bgColor: 'bg-accent-surface-red',
          textColor: 'text-accent-status-red',
          icon: 'Info' as const,
          iconColor: 'text-accent-status-red',
        };
      case 'failed-2nd-attempt':
        return {
          label: label || TAG_LABELS.FAIL_2ND_ATTEMPT,
          bgColor: 'bg-accent-surface-red',
          textColor: 'text-accent-status-red',
          icon: 'Info' as const,
          iconColor: 'text-accent-status-red',
        };
      case 'no-attempts':
        return {
          label: label || TAG_LABELS.NO_ATTEMPTS,
          bgColor: 'bg-accent-surface-red',
          textColor: 'text-accent-status-red',
          icon: 'Info' as const,
          iconColor: 'text-accent-status-red',
        };
      case 'failed-practice':
        return {
          label: label || TAG_LABELS.FAIL_PRACTICE,
          bgColor: 'bg-accent-surface-red',
          textColor: 'text-accent-status-red',
          icon: 'Info' as const,
          iconColor: 'text-accent-status-red',
        };
      case 'pass':
        return {
          label: label || TAG_LABELS.PASS,
          bgColor: 'bg-accent-surface-green',
          textColor: 'text-neutral-black',
          icon: undefined,
          iconColor: undefined,
        };
      case 'fail':
        return {
          label: label || TAG_LABELS.FAIL,
          bgColor: 'bg-accent-surface-red',
          textColor: 'text-accent-status-red',
          icon: undefined,
          iconColor: undefined,
        };
      default:
        return {
          label: label || WORDS.UNKNOWN,
          bgColor: 'bg-neutral-gray-100',
          textColor: 'text-neutral-gray-600',
          icon: undefined,
          iconColor: undefined,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div
      className={cn(
        'inline-flex flex-shrink-0 items-center justify-center gap-1',
        'overflow-hidden rounded-md px-2 py-1',
        config.bgColor,
        className,
      )}
    >
      {config.icon && (
        <>
          <Icon name={config.icon} size={12} className={config.iconColor} />
        </>
      )}
      <span
        className={cn(
          'whitespace-nowrap text-center text-xs font-medium leading-4',
          config.textColor,
        )}
      >
        {config.label}
      </span>
    </div>
  );
};

StatusBadge.displayName = 'StatusBadge';
